import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Download, Layers, RefreshCw } from 'lucide-react';
import { apiErrorMessage, getStrats } from '../api/abfApi';
import { facilityWorkspacePath } from '../routes';
import type { ActionFeedbackState } from '../components/ActionFeedback';
import type { StratDimension, StratResponse, StratRow } from '../types/collateralExplorer';
import { signedPts } from '../presentation/format';

/**
 * WS2 — Stratification of the released pool (Oaktree row 2.10). Every figure is a stored
 * strat row written when the tape was released; the panel groups nothing itself. A row of
 * a categorical dimension opens the Collateral Explorer narrowed to that bucket.
 */

interface Props {
  facilityId: number;
  tapeIngestId?: number;
  currency?: string | null;
  onFeedback: (feedback: ActionFeedbackState | null) => void;
}

const DIMENSION_LABELS: Record<StratDimension, string> = {
  VINTAGE: 'Vintage', STATE: 'State', COUNTRY: 'Country', OBLIGOR: 'Obligor (top 25)', INDUSTRY: 'Industry', RATING: 'Rating',
  ASSET_TYPE: 'Asset type', FICO_BAND: 'FICO band', LTV_BAND: 'LTV band', BALANCE_BAND: 'Balance band', RATE_BAND: 'Rate band',
  REMAINING_TERM_BAND: 'Remaining term', PAYMENT_FREQUENCY: 'Payment frequency', AMORTIZATION_TYPE: 'Amortization type', DELINQUENCY: 'Delinquency',
};
/** Dimensions the explorer can be narrowed to (bands are not loan columns). */
const EXPLORER_DIMENSION: Partial<Record<StratDimension, string>> = {
  VINTAGE: 'VINTAGE', STATE: 'STATE', COUNTRY: 'COUNTRY', OBLIGOR: 'OBLIGOR', INDUSTRY: 'INDUSTRY', RATING: 'RATING',
  ASSET_TYPE: 'ASSET_TYPE', PAYMENT_FREQUENCY: 'PAYMENT_FREQUENCY', AMORTIZATION_TYPE: 'AMORTIZATION_TYPE', DELINQUENCY: 'DELINQUENCY',
};

const Panel = styled.section`
  margin-top: 14px; padding: 1rem; border: 1px solid rgba(148,163,184,.16); border-radius: 12px; background: rgba(8,16,30,.8);
  h3 { margin: 0; color: #f8fafc; font-size: 15px; }
  p.sub { margin: .3rem 0 0; color: #94a3b8; font-size: 12px; }
  .toolbar { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-top: .8rem; }
  /* Label beside the dropdown, so the dropdown and the buttons share one line. */
  .toolbar label { display: flex; flex-direction: row; align-items: center; gap: 8px; color: #94a8c2; font-size: 12px; }
  select { min-height: 36px; padding: .4rem .55rem; border: 1px solid rgba(148,163,184,.2); border-radius: 9px; background: #07101f; color: #e2e8f0; font-size: 13px; }
  button { display: inline-flex; align-items: center; gap: 6px; min-height: 36px; padding: .4rem .7rem; border: 1px solid rgba(148,163,184,.24); border-radius: 8px; color: #cbd5e1; background: rgba(15,23,42,.6); cursor: pointer; font: inherit; font-size: 13px; }
  button:disabled { opacity: .5; cursor: default; }
  table { width: 100%; border-collapse: collapse; margin-top: .8rem; font-size: 12px; font-variant-numeric: tabular-nums; }
  th { text-align: left; padding: .5rem .55rem; color: #7dd3fc; font-weight: 650; text-transform: uppercase; letter-spacing: .05em; font-size: 11px; border-bottom: 1px solid rgba(148,163,184,.2); }
  td { padding: .5rem .55rem; border-top: 1px solid rgba(148,163,184,.1); color: #e2e8f0; }
  th.num, td.num { text-align: right; white-space: nowrap; }
  tr.link td:first-child { color: #9bdef5; text-decoration: underline; text-underline-offset: 3px; cursor: pointer; }
  .muted { color: #94a3b8; }
  .boundary { margin-top: .7rem; color: #94a3b8; font-size: 11px; line-height: 1.5; }
  .legend { display: flex; gap: 16px; margin: 4px 0 0 44px; color: #94a3b8; font-size: 11px; }
  .legend span { display: inline-flex; align-items: center; gap: 6px; }
  .legend i { display: inline-block; width: 10px; height: 10px; border-radius: 2px; }
`;

const money = (v: unknown) => v === null || v === undefined ? '—' : Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 });
const pct = (v: unknown, places = 2) => v === null || v === undefined ? '—' : `${Number(v).toFixed(places)}%`;
/** DQ30 above this share of the bucket's balance is highlighted in the chart and the table. */
const DQ30_HIGHLIGHT_PCT = 5;
const delta = (v: unknown, unit: 'pts' | 'money') => v === null || v === undefined ? '—'
  : unit === 'pts' ? signedPts(Number(v)) : `${Number(v) >= 0 ? '+' : '−'}${money(Math.abs(Number(v)))}`;

const StratificationPanel: React.FC<Props> = ({ facilityId, tapeIngestId, currency, onFeedback }) => {
  const navigate = useNavigate();
  const [dimension, setDimension] = useState<StratDimension>('STATE');
  const [model, setModel] = useState<StratResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setModel(await getStrats(facilityId, tapeIngestId, dimension));
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'Stratification could not be loaded.') });
    } finally {
      setLoading(false);
    }
  }, [dimension, facilityId, onFeedback, tapeIngestId]);
  useEffect(() => { void load(); }, [load]);

  const rows = useMemo(() => model?.rows ?? [], [model]);
  const chart = useMemo(() => rows.map(r => ({ name: r.bucket_label, pct: r.pct_of_pool == null ? 0 : Number(r.pct_of_pool), dq: r.delinquency_pct == null ? 0 : Number(r.delinquency_pct) })), [rows]);
  const linkable = EXPLORER_DIMENSION[dimension];

  const open = (row: StratRow) => {
    if (!linkable || !model?.snapshot) return;
    const q = new URLSearchParams({ collateralView: 'explorer', tape: String(model.snapshot.tape_ingest_id), poolDimension: linkable, poolBucket: row.bucket_label });
    navigate(`${facilityWorkspacePath(facilityId, 'collateral')}?${q}`);
  };
  const exportCsv = () => {
    if (!model?.snapshot) return;
    const head = ['dimension', 'bucket', 'loans', 'balance', 'pct_of_pool', 'eligible_balance', 'eligible_pct', 'dq30_pct', 'net_loss_pct', 'prior_balance', 'balance_delta', 'pct_of_pool_delta'];
    const body = rows.map(r => [r.strat_type, r.bucket_label, r.asset_count, r.balance, r.pct_of_pool, r.eligible_balance, r.eligible_pct, r.delinquency_pct, r.net_loss_rate_pct, r.prior_balance, r.balance_delta, r.pct_of_pool_delta]
      .map(v => v === null || v === undefined ? '' : /[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v)).join(','));
    const blob = new Blob([[head.join(','), ...body].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `strats_${facilityId}_${dimension}_${model.snapshot.as_of_date}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Panel aria-label="Stratification" aria-busy={loading}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h3><Layers size={15} style={{ verticalAlign: 'middle', marginRight: 6 }} />Stratification</h3>
          <p className="sub">{model?.snapshot
            ? `Released tape as of ${model.snapshot.as_of_date} · ${money(model.snapshot.asset_count)} loans · ${money(model.snapshot.collateral_balance)} ${currency || ''}${model.priorSnapshot ? ` · compared with ${model.priorSnapshot.as_of_date}` : ' · first snapshot, no prior to compare'}`
            : model?.message || 'Loading…'}</p>
        </div>
        <div className="toolbar" style={{ marginTop: 0 }}>
          <label>Dimension
            <select aria-label="Stratification dimension" value={dimension} onChange={e => setDimension(e.target.value as StratDimension)}>
              {(model?.dimensions ?? (Object.keys(DIMENSION_LABELS) as StratDimension[])).map(d => <option key={d} value={d}>{DIMENSION_LABELS[d] || d}</option>)}
            </select>
          </label>
          <button type="button" onClick={() => void load()} disabled={loading}><RefreshCw size={14} />Refresh</button>
          <button type="button" onClick={exportCsv} disabled={!rows.length}><Download size={14} />Export CSV</button>
        </div>
      </div>

      {rows.length > 0 && (
        <div style={{ height: 184, marginTop: 10 }} aria-hidden="true">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <XAxis dataKey="name" stroke="#64748b" fontSize={10} interval={0} tickFormatter={(v: string) => v.length > 12 ? v.slice(0, 11) + '…' : v} />
              <YAxis stroke="#64748b" fontSize={10} tickFormatter={(v: number) => `${v}%`} width={40} />
              <Tooltip formatter={(value: unknown, name: unknown) => [`${Number(value).toFixed(2)}%`, String(name) === 'pct' ? '% of pool' : 'DQ30 %']} contentStyle={{ background: '#0b1526', border: '1px solid #2b3b50', fontSize: 12 }} />
              <Bar dataKey="pct" isAnimationActive={false}>{chart.map((c, i) => <Cell key={i} fill={c.dq > DQ30_HIGHLIGHT_PCT ? '#c9786a' : '#5b8fc7'} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="legend" aria-hidden="true">
            <span><i style={{ background: '#5b8fc7' }} /> % of pool by balance</span>
            <span><i style={{ background: '#c9786a' }} /> DQ30 above {DQ30_HIGHLIGHT_PCT}% of the bucket</span>
          </div>
        </div>
      )}

      <table>
        <thead><tr>
          <th>{DIMENSION_LABELS[dimension]}</th><th className="num">Loans</th><th className="num">Balance</th><th className="num">% of pool</th>
          <th className="num">Eligible %</th><th className="num">DQ30 %</th><th className="num">Net loss %</th><th className="num">Δ balance</th><th className="num">Δ % of pool</th>
        </tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.bucket_label} className={linkable ? 'link' : undefined} onClick={() => open(r)} title={linkable ? `Open the loans in ${r.bucket_label}` : undefined}>
              <td>{r.bucket_label}</td>
              <td className="num">{money(r.asset_count)}</td>
              <td className="num">{money(r.balance)}</td>
              <td className="num">{pct(r.pct_of_pool)}</td>
              <td className="num">{pct(r.eligible_pct, 1)}</td>
              <td className="num" style={{ color: r.delinquency_pct != null && Number(r.delinquency_pct) > DQ30_HIGHLIGHT_PCT ? '#fda4af' : undefined }}>{pct(r.delinquency_pct)}</td>
              <td className="num">{pct(r.net_loss_rate_pct)}</td>
              <td className="num muted">{delta(r.balance_delta, 'money')}</td>
              <td className="num muted">{delta(r.pct_of_pool_delta, 'pts')}</td>
            </tr>
          ))}
          {!rows.length && !loading && <tr><td colSpan={9} className="muted" style={{ padding: '1.2rem', textAlign: 'center' }}>{model?.message || 'No stratification rows for this dimension.'}</td></tr>}
        </tbody>
      </table>
      {model?.authorityBoundary && <div className="boundary">{model.authorityBoundary}{linkable ? ' Click a row to open its loans in the explorer.' : ' Banded dimensions are not loan columns, so they do not open the explorer.'}</div>}
    </Panel>
  );
};

export default StratificationPanel;
